'use client';

import classNames from 'classnames';
import { clamp } from 'lodash';
import { MoveRightIcon } from 'lucide-react';

import { BrowserWindow } from '@/components/BrowserWindow/BrowserWindow';
import { Link } from '@/components/Link/Link';
import { UploadcareImage } from '@/components/UploadcareImage';
import { ExampleModal } from '@/src/ui/ReferenceCards/components/ExampleModal';
import type * as Payload from '@/types';
import { Button } from '@/ui/Button/Button';
import { createNextRouteHref } from '@/util/routing/createNextRouteHref';

import { PressReleaseExample } from '../../PressReleaseExample/PressReleaseExample';
import { usePressReleaseExampleModal } from '../../PressReleaseExample/usePressReleaseExampleModal';

import styles from './ExampleBlock.module.scss';

interface Props {
    node: Payload.Example;
    layout?: 'contained' | 'expanded' | 'full-width';
    searchParams?: URLSearchParams;
}

export function ExampleBlock({ node, searchParams, layout }: Props) {
    const { title, screenshot, customer, color, logo } = node;
    const modal = usePressReleaseExampleModal(node);

    return (
        <>
            <Link
                className={classNames(styles.container, color, {
                    [styles.expanded]: layout === 'expanded',
                })}
                href={`${createNextRouteHref(node)}${
                    searchParams?.size ? `?${searchParams.toString()}` : ''
                }`}
                title={title}
                onClick={modal.open}
            >
                <div className={styles.header}>
                    {logo && (
                        <UploadcareImage
                            loading="lazy"
                            src={logo.url!}
                            alt={customer ?? 'Company logo'}
                            className={styles.logo}
                            width={logo.width}
                            height={logo.height}
                        />
                    )}
                    <h4 className={styles.title}>{`Example: ${title}`}</h4>
                </div>
                <BrowserWindow className={styles.browser}>
                    {screenshot && (
                        <UploadcareImage
                            className={styles.image}
                            sizes="80vw, (min-width: 768px) 90vw, (min-width: 992px) 55vw, (min-width: 1200px) 65vw, (min-width: 1600px) 1150px"
                            src={screenshot.url!}
                            alt={title}
                            width={1920}
                            height={1200}
                            loader={({ src, width }) =>
                                `${src}-/scale_crop/1920x1200/-/resize/${clamp(width, 0, 1920)}/`
                            }
                        />
                    )}
                </BrowserWindow>
                <div className={styles.overlay}>
                    <Button
                        className={styles.button}
                        iconRight={MoveRightIcon}
                        variant="secondary"
                        size="large"
                    >
                        See full example
                    </Button>
                </div>
            </Link>
            <ExampleModal
                title={title}
                content={<PressReleaseExample example={node} isModal />}
                open={modal.isOpen}
                onClose={modal.close}
            />
        </>
    );
}
